import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, input, output } from '@angular/core';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { TranslateModule } from '@ngx-translate/core';

import { Facet } from 'ish-core/models/facet/facet.model';
import { Filter } from 'ish-core/models/filter/filter.model';
import { SanitizePipe } from 'ish-core/pipes/sanitize.pipe';
import { URLFormParams } from 'ish-core/utils/url-form-params';

/**
 * The Filter Text Component displays a filter group. The facets of the filter group are presented as links with optional clear-button.
 *
 * @example
 * <ish-filter-text
 *   [filterElement]="element"
 *   (applyFilter)="applyFilter($event)">
 * </ish-filter-text>
 */
@Component({
  selector: 'ish-filter-text',
  templateUrl: './filter-text.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, FaIconComponent, SanitizePipe, TranslateModule],
})
export class FilterTextComponent implements OnInit {
  readonly filterElement = input.required<Filter>();
  readonly applyFilter = output<{
    searchParameter: URLFormParams;
  }>();

  /**
   * two-way-binding (banana in a box) [(showAll)]="showAllElements[element.name]"
   */
  readonly showAllChange = output<boolean>();
  private showAllValue = false;
  @Input()
  get showAll() {
    return this.showAllValue;
  }
  set showAll(val) {
    this.showAllValue = val;
    this.showAllChange.emit(this.showAllValue);
  }

  maxLevel = 0;
  facets: Facet[] = [];

  ngOnInit() {
    this.maxLevel = Math.max(...this.filterElement().facets.map(o => o.level)) || 0;

    this.facets = this.filterElement().facets.filter(x => x.selected || !this.maxLevel || x.level >= this.maxLevel);
  }

  filter(facet: Facet) {
    this.applyFilter.emit({ searchParameter: facet.searchParameter });
  }

  /**
   * sort selected to top, increase limitCount to selectedCount on showLess
   */
  getFacets() {
    const facets = [...this.facets];

    const filterElement = this.filterElement();
    if (this.showAll || filterElement.limitCount === -1) {
      return facets;
    }

    const selectedFacetsCount = facets.filter(x => x.selected).length;

    return facets
      .sort((a, b) => (a.selected > b.selected ? -1 : a.selected < b.selected ? 1 : 0))
      .slice(0, Math.max(filterElement.limitCount || 0, selectedFacetsCount));
  }
}
